import React, { memo, useState, useMemo } from 'react';

const FILL_COLORS = ['#F5F5F5', 'var(--mantine-color-green-2)', 'var(--mantine-color-green-4)', 'var(--mantine-color-green-5)', 'var(--mantine-color-green-7)'];
const BORDER_COLORS = ['#E5E5E5', 'var(--mantine-color-green-2)', 'var(--mantine-color-green-3)', 'var(--mantine-color-green-4)', 'var(--mantine-color-green-4)'];

/**
 * Determine fill and border colors manually based on count.
 */
export function getColorFromCount(count) {
	const c = count || 0;
	if (c >= 20) return { fill: FILL_COLORS[4], border: BORDER_COLORS[4] };
	if (c >= 10) return { fill: FILL_COLORS[3], border: BORDER_COLORS[3] };
	if (c >= 5) return { fill: FILL_COLORS[2], border: BORDER_COLORS[2] };
	if (c >= 1) return { fill: FILL_COLORS[1], border: BORDER_COLORS[1] };
	if (c < 0) return { fill: '#FFF', border: 'var(--mantine-color-red-2)' };
	return { fill: FILL_COLORS[0], border: BORDER_COLORS[0] };
}

const StationHeatmap = memo(({
	station,
	squaresPerColumn = 6,
	squareSize = 10,
	squarePadding = 2,
}) => {
	const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, item: {} });
	// get the last 336 slots
	const slots = station.slots.slice(-336) || [];
	const rows = squaresPerColumn;
	const columns = Math.ceil(slots.length / rows);

	const cells = useMemo(() =>
		slots.map((slot, i) => {
			const col = Math.floor(i / rows);
			const row = i % rows;
			const { fill, border } = getColorFromCount(slot.count);
			return {
				_id: slot._id,
				x: col * (squareSize + squarePadding),
				y: row * (squareSize + squarePadding),
				fill,
				border,
				count: slot.count || 0,
				date: slot.date ? new Date(slot.date).toLocaleString(undefined, {
					year: 'numeric',
					month: 'numeric',
					day: 'numeric',
					hour: 'numeric',
					minute: '2-digit',
					hour12: true // Set to false if you want 24-hour format
				}) : '',
				gap: slot.gap || 0,
			};
		}),
		[slots, rows, squareSize, squarePadding]
	);

	const width = columns * (squareSize + squarePadding) - squarePadding;
	const height = rows * (squareSize + squarePadding) - squarePadding;

	const handleClick = (e, c) => {
		console.log('Clicked cell:', c);
		window.location.href = `/recording/${c._id}`;
	}

	const handleMouseEnter = (e, c) => {
		const rect = e.currentTarget.getBoundingClientRect();
		setTooltip({
			visible: true,
			x: rect.left + rect.width / 2,
			y: rect.top - 8,
			item: c,
		});
	};

	const handleMouseMove = (e) => {
		setTooltip(t => ({ ...t, x: e.clientX, y: e.clientY }));
	};

	const handleMouseLeave = () => {
		setTooltip(t => ({ ...t, visible: false }));
	};

	return (
		<div style={{ position: 'relative', display: 'inline-block' }}>
			<svg width={width} height={height}>
				{cells.map((c, idx) => (
					<g key={idx}>
						<rect
							x={c.x}
							y={c.y}
							width={squareSize}
							height={squareSize}
							fill={c.fill}
							stroke={c.border}
							rx={2}
							ry={2}
							onMouseEnter={e => handleMouseEnter(e, c)}
							onMouseMove={handleMouseMove}
							onMouseLeave={handleMouseLeave}
							onClick={e => handleClick(e, c)}
						/>
						{c.count < 0 && (
							<line
								x1={c.x}
								y1={c.y + squareSize}
								x2={c.x + squareSize}
								y2={c.y}
								stroke={c.border}
								strokeWidth={1}
								onMouseEnter={e => handleMouseEnter(e, c)}
								onMouseMove={handleMouseMove}
								onMouseLeave={handleMouseLeave}
								onClick={e => handleClick(e, c)}
							/>
						)}
					</g>
				))}
			</svg>

			{tooltip.visible && (
				<div
					style={{
						position: 'fixed',
						top: tooltip.y - 30,
						left: tooltip.x,
						background: 'rgba(0,0,0,0.75)',
						color: '#fff',
						padding: '2px 6px',
						borderRadius: '3px',
						pointerEvents: 'none',
						fontSize: '10px',
					}}
				>
					<b>{tooltip.item.date}</b>
					<br />
					{tooltip.item.count + (tooltip.item.count >= 0 ? ' matches' : ' - gap: ' + tooltip.item.gap + ' mins')}
				</div>
			)}
		</div>
	);
});

export default StationHeatmap;
